import { FC } from 'react'
import { useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'

import { ContributionPoint, ContributionPointBaseObject } from '@img-arena/consoles-api-streaming'
import { SportPropertySelect } from '@/components/inputs/SportPropertySelect'
import { streamingRestApi } from '@/service/streamingApi'
import { FormDialogProps } from '@/types/forms'
import { formikSaveButtonProps, onFieldChange } from '@/utils/forms/formik'
import {
  FOLLOW_PROPERTY_TOURNAMENT,
  FormattedContributionPointData
} from '@/views/Admin/ContributionPoints/ContributionPointsView'

import { ConsoleDialog } from '../../ConsoleDialog'
import { DynamicFieldsList } from '../../inputs/DynamicFieldsList/DynamicFieldsList'
import { PropertyTournamentSelect } from '../../inputs/PropertyTournamentSelect/PropertyTournamentSelect'
import { addContributionPointsValidationSchema } from './addContributionPointForm.validation'
import { ADORNMENT, FIELDS_AMOUNT_LABEL, makeSubdomainFieldLabel, TITLE_LABEL } from './constants'

export type AddContributionPointFormValues = {
  sport: ContributionPointBaseObject | null
  property: ContributionPointBaseObject | null
  tournament: ContributionPointBaseObject | null
  fields: string[]
}

type AddContributionPointFormProps = FormDialogProps<
  FormattedContributionPointData | ContributionPoint
>

export const AddContributionPointForm: FC<AddContributionPointFormProps> = ({
  onClose,
  onSubmit,
  open,
  defaultValues
}) => {
  const contributionPointsEndpoints = streamingRestApi.endpoints.contributionPoints
  const isEditing = !!defaultValues

  const formik = useFormik<AddContributionPointFormValues>({
    initialValues: {
      sport: defaultValues?.property?.sport || null,
      property: defaultValues?.property || null,
      tournament: defaultValues?.tournament || null,
      fields: []
    },
    validationSchema: addContributionPointsValidationSchema,
    onSubmit: async (values) => {
      const { property, fields, tournament } = values
      if (!property) return
      const names = fields.filter((val) => !!val)
      try {
        if (tournament && tournament.id !== FOLLOW_PROPERTY_TOURNAMENT.id) {
          await contributionPointsEndpoints.addTournamentContributionPoint({
            id: tournament.id,
            names
          })
        } else {
          await contributionPointsEndpoints.addContributionPoint({ id: property.id, names })
        }

        formik.resetForm()
        enqueueSnackbar('Contribution points added successfully', { variant: 'success' })
        onSubmit?.()
        onClose?.()
      } catch (error) {
        enqueueSnackbar(String(error), { variant: 'error' })
        throw Error()
      }
    }
  })

  const { sport, tournament, property, fields } = formik.values

  return (
    <ConsoleDialog
      open={!!open}
      title={TITLE_LABEL}
      onClose={() => {
        formik.resetForm()
        onClose?.()
      }}
      SaveButtonProps={{ ...formikSaveButtonProps(formik), text: 'Confirm' }}
    >
      <SportPropertySelect
        value={{ sport, property }}
        onChange={{
          sport: (sport) => {
            onFieldChange(formik, 'sport', sport)
            onFieldChange(formik, 'property', null)
            onFieldChange(formik, 'tournament', null)
          },
          property: (property) => {
            onFieldChange(formik, 'property', property)
            onFieldChange(formik, 'tournament', null)
          }
        }}
        onClear={{
          sport: () => {
            onFieldChange(formik, 'sport', null)
            onFieldChange(formik, 'property', null)
            onFieldChange(formik, 'tournament', null)
          },
          property: () => {
            onFieldChange(formik, 'property', null)
            onFieldChange(formik, 'tournament', null)
          }
        }}
        readOnly={isEditing}
        required
      />
      <PropertyTournamentSelect
        tournament={tournament}
        onChange={(tournament) => {
          onFieldChange(formik, 'tournament', tournament)
        }}
        onClear={() => onFieldChange(formik, 'tournament', null)}
        readOnly={isEditing}
        property={property}
        disabled={!property}
      />
      <DynamicFieldsList
        fieldAmountTextFieldProps={{
          label: FIELDS_AMOUNT_LABEL
        }}
        values={fields}
        onChange={(fields) => {
          onFieldChange(formik, 'fields', fields)
        }}
        formatListOption={makeSubdomainFieldLabel}
        endAdornment={ADORNMENT}
      />
    </ConsoleDialog>
  )
}
